import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Scale, FileText, Lightbulb, Search, Users, GraduationCap,
  Clock, Globe, RefreshCw, Star, Target, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AnimateIn } from '../components/Layout';
import { profileData, trustMetrics, coreExpertise, services, whyChooseMe } from '../data/mock';

const iconMap = {
  Scale, FileText, Lightbulb, Search, Users, GraduationCap,
  Clock, Globe, RefreshCw, Star, Target, CheckCircle,
};

const Home = () => {
  return (
    <div>
      {/* Hero Section — with About on right */}
      <section className="relative min-h-screen flex items-center bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#F8F9FA] to-transparent pointer-events-none" />
        <div className="absolute bottom-24 left-6 md:left-12 w-20 h-[2px] bg-[#C9A646] hidden md:block" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-20">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
            {/* Left — Hero Text */}
            <div className="flex-1 lg:max-w-[55%]">
              <AnimateIn>
                <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-8 block">
                  Labour Law Consultant
                </span>
              </AnimateIn>
              <AnimateIn delay={150}>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#0B1F3A] leading-[1.1] mb-8">
                  Labour Law Expert.
                  <br />
                  <span className="text-[#C9A646]">PAN India</span> Compliance.
                </h1>
              </AnimateIn>
              <AnimateIn delay={300}>
                <p className="text-lg md:text-xl text-gray-500 font-body leading-relaxed mb-10 max-w-xl">
                  {profileData.tagline}. Trusted by 100+ organizations across India.
                </p>
              </AnimateIn>
              <AnimateIn delay={450}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/consultation">
                    <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12 text-base w-full sm:w-auto">
                      Schedule Consultation
                    </Button>
                  </Link>
                  <Link to="/expertise">
                    <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-body rounded-sm px-8 h-12 text-base w-full sm:w-auto">
                      View Expertise <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </AnimateIn>
            </div>

            {/* Right — Photo & About */}
            <div className="flex-shrink-0 lg:max-w-[40%] flex flex-col items-center text-center">
              <AnimateIn delay={300}>
                <div className="w-52 h-52 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-full bg-[#0B1F3A] flex items-center justify-center shadow-2xl overflow-hidden ring-4 ring-[#C9A646]/20 mb-6">
                  {profileData.photo ? (
                    <img src={profileData.photo} alt={profileData.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[#C9A646] text-6xl md:text-7xl font-heading font-bold">{profileData.initials}</span>
                  )}
                </div>
              </AnimateIn>
              <AnimateIn delay={450}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-2">{profileData.name}</h2>
                <p className="text-[#C9A646] font-body font-semibold text-sm tracking-wider mb-4">{profileData.title} &bull; {profileData.subtitle}</p>
                <p className="text-gray-500 font-body text-sm leading-relaxed max-w-xs mx-auto mb-4">
                  {profileData.bio}
                </p>
                <Link to="/about" className="text-[#C9A646] font-body font-semibold text-sm flex items-center justify-center gap-2 hover:gap-3 transition-all group">
                  Learn more <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </AnimateIn>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Metrics */}
      <section className="bg-[#0B1F3A] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {trustMetrics.map((metric, idx) => (
              <AnimateIn key={idx} delay={idx * 100}>
                <div className="text-center">
                  <span className="text-[#C9A646] text-4xl md:text-5xl font-heading font-bold block">{metric.value}</span>
                  <p className="text-white font-body font-medium mt-2 text-sm md:text-base">{metric.label}</p>
                  <p className="text-gray-400 font-body text-xs md:text-sm mt-1">{metric.description}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* Core Expertise */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="text-center mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">What I Do</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3">Core Expertise</h2>
              <div className="w-16 h-[2px] bg-[#C9A646] mx-auto mt-4" />
            </div>
          </AnimateIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreExpertise.map((item, idx) => {
              const Icon = iconMap[item.icon];
              return (
                <AnimateIn key={idx} delay={idx * 100}>
                  <div className="bg-white p-8 rounded-sm hover:shadow-lg transition-shadow duration-300 group">
                    <div className="w-12 h-12 rounded-full bg-[#C9A646]/10 flex items-center justify-center mb-5 group-hover:bg-[#C9A646]/20 transition-colors duration-300">
                      {Icon && <Icon size={22} className="text-[#C9A646]" />}
                    </div>
                    <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-3">{item.title}</h3>
                    <p className="text-gray-500 font-body text-sm leading-relaxed">{item.description}</p>
                  </div>
                </AnimateIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="text-center mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">How I Help</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3">Services</h2>
              <div className="w-16 h-[2px] bg-[#C9A646] mx-auto mt-4" />
            </div>
          </AnimateIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, idx) => (
              <AnimateIn key={service.id} delay={idx * 100}>
                <div className="border border-gray-100 p-7 rounded-sm hover:border-[#C9A646]/30 hover:shadow-md transition-all duration-300 group">
                  <h3 className="font-heading text-lg font-bold text-[#0B1F3A] mb-3 group-hover:text-[#C9A646] transition-colors duration-300">{service.title}</h3>
                  <p className="text-gray-500 font-body text-sm leading-relaxed">{service.description}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
          <AnimateIn delay={300}>
            <div className="text-center mt-12">
              <Link to="/services">
                <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white font-body rounded-sm px-8 h-11">
                  View All Services <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* Why Choose Me */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="text-center mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Why Clients Trust Me</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3">Why Choose Me</h2>
              <div className="w-16 h-[2px] bg-[#C9A646] mx-auto mt-4" />
            </div>
          </AnimateIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {whyChooseMe.map((item, idx) => {
              const Icon = iconMap[item.icon];
              return (
                <AnimateIn key={idx} delay={idx * 100}>
                  <div className="flex gap-5">
                    <div className="w-11 h-11 rounded-full bg-[#0B1F3A] flex items-center justify-center shrink-0 mt-1">
                      {Icon && <Icon size={18} className="text-[#C9A646]" />}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#0B1F3A] mb-2">{item.title}</h3>
                      <p className="text-gray-500 font-body text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </AnimateIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 md:py-24 bg-[#0B1F3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase mb-4 block">Get Started</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Need Compliance Support?
            </h2>
            <p className="text-gray-400 font-body text-lg max-w-2xl mx-auto mb-10">
              Let's discuss your specific compliance requirements and find the right solution for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consultation">
                <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12 text-base w-full sm:w-auto">
                  Book Consultation
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0B1F3A] font-body rounded-sm px-8 h-12 text-base w-full sm:w-auto">
                  Get In Touch
                </Button>
              </Link>
            </div>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default Home;
